import React, { useEffect, useState } from "react";
import { X, Mic, Square, ShieldCheck, Loader2, FileText } from "lucide-react";
import { SAMPLE_INTERVIEWS } from "../admin/interviewSamples";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// Preview only: no audio is captured; shows the planned recording flow.
export default function InterviewRecorder({ onClose, onUseTranscript }) {
  const [consent, setConsent] = useState(false);
  const [stage, setStage] = useState("ready");
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (stage !== "recording") return undefined;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [stage]);
  useEffect(() => {
    if (stage !== "processing") return undefined;
    const t = setTimeout(() => setStage("done"), 2200);
    return () => clearTimeout(t);
  }, [stage]);
  const sample = SAMPLE_INTERVIEWS[0];

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 sm:items-center sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="interview-recorder" className="w-full max-w-md rounded-t-3xl bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl sm:rounded-3xl">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[var(--navy)]">Record an interview</h3>
          <button onClick={onClose} aria-label="Close" data-testid="recorder-close" className="text-slate-400"><X size={22} /></button>
        </div>
        <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500" data-testid="recorder-preview-tag">Preview</span>

        {stage === "ready" && (
          <div className="mt-4">
            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 p-3 text-sm text-slate-700">
              <input type="checkbox" checked={consent} onChange={() => setConsent(!consent)} data-testid="recorder-consent" className="mt-0.5 h-5 w-5 accent-[var(--gold)]" />
              <span>Everyone in this conversation has agreed to be recorded.</span>
            </label>
            <button disabled={!consent} onClick={() => setStage("recording")} data-testid="recorder-start"
              className="mx-auto mt-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-600 text-white shadow-lg ring-8 ring-red-100 transition-transform active:scale-95 disabled:bg-slate-300 disabled:ring-slate-100">
              <Mic size={34} />
            </button>
            <p className="mt-3 text-center text-xs text-slate-500">{consent ? "Tap to start recording" : "Confirm permission to enable recording"}</p>
          </div>
        )}

        {stage === "recording" && (
          <div className="mt-6 text-center">
            <p className="flex items-center justify-center gap-2 text-sm font-bold text-red-600"><span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-600" /> Recording</p>
            <p className="mt-2 font-mono text-5xl font-bold text-[var(--navy)]" data-testid="recorder-timer">{fmt(secs)}</p>
            <div className="mt-5 flex h-12 items-center justify-center gap-1">
              {Array.from({ length: 28 }).map((_, i) => (
                <span key={i} className="w-1 animate-pulse rounded-full bg-[var(--gold)]" style={{ height: `${20 + ((i * 37) % 70)}%`, animationDelay: `${(i % 7) * 0.12}s` }} />
              ))}
            </div>
            <button onClick={() => setStage("processing")} data-testid="recorder-stop" className="mx-auto mt-6 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--navy)] text-white shadow-lg active:scale-95">
              <Square size={26} fill="currentColor" />
            </button>
            <p className="mt-3 text-xs text-slate-500">Tap to stop. The recording saves to your interviews.</p>
          </div>
        )}

        {stage === "processing" && (
          <div className="mt-10 flex flex-col items-center pb-8 text-center" data-testid="recorder-processing">
            <Loader2 size={34} className="animate-spin text-[var(--gold)]" />
            <p className="mt-3 font-semibold text-[var(--navy)]">Preparing your transcript…</p>
            <p className="text-xs text-slate-500">Usually ready in a minute or two. You can keep working.</p>
          </div>
        )}

        {stage === "done" && (
          <div className="mt-4" data-testid="recorder-done">
            <p className="text-sm font-semibold text-emerald-700">Transcript ready · {fmt(Math.max(secs, 1))}</p>
            <div className="mt-2 max-h-56 space-y-2 overflow-y-auto rounded-2xl bg-slate-50 p-3">
              {sample.lines.slice(0, 4).map((l, i) => (
                <p key={i} className="text-sm text-slate-700"><span className="mr-1 font-mono text-[11px] text-slate-400">{l.t}</span><b className="text-[var(--navy)]">{l.who}:</b> {l.text}</p>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={onClose} data-testid="recorder-save" className="rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-600">Save to interviews</button>
              <button onClick={() => { onUseTranscript?.(sample.lines.map((l) => `${l.who}: ${l.text}`).join(" ")); onClose(); }} data-testid="recorder-use-transcript"
                className="flex items-center justify-center gap-1.5 rounded-xl bg-[var(--navy)] py-2.5 text-sm font-semibold text-white"><FileText size={15} /> Use in my story</button>
            </div>
          </div>
        )}
        <p className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck size={13} /> Only you and your editors can hear or read this interview.</p>
      </div>
    </div>
  );
}
